// Die beiden Editoren der Vorlagen — in der Seite, nicht in einem Dialog
// (`?category=<id>|new`, `?template=<id>|new`).

import { useState } from "react";
import { ApiError, del, patch, post } from "../../api/client";
import type { PromptTemplate, TemplateCategory } from "../../api/types";
import { errorText } from "../../lib/labels";
import { Link } from "../../router/router";
import { Card } from "../../ui/card";
import { ConfirmButton } from "../../ui/confirm-button";
import { Input, Textarea } from "../../ui/field";
import { SelectMenu } from "../../ui/select-menu";
import { toast } from "../../ui/toaster";
import { TEMPLATE_ERRORS, categoryBody, categoryFrom, templateBody, templateFrom, txt, type CategoryState, type TemplateState } from "./templates-map";

const failText = (err: unknown) => (err instanceof ApiError ? TEMPLATE_ERRORS[err.code] : undefined) ?? errorText(err);

interface EditorProps {
  base: string;
  here: string;
  /** Nach Speichern/Löschen: Liste frisch holen und dorthin zurück. */
  done: () => void;
}

export function CategoryEditor({ category, categories, base, here, done }: EditorProps & { category: TemplateCategory | null; categories: TemplateCategory[] }) {
  const [s, setS] = useState<CategoryState>(() => categoryFrom(category, categories));
  const [error, setError] = useState("");
  const set = (p: Partial<CategoryState>) => setS((cur) => ({ ...cur, ...p }));
  const save = async () => {
    try {
      if (category) await patch(`${base}/categories/${category.id}`, categoryBody(s)); else await post(`${base}/categories`, categoryBody(s));
      toast("Gespeichert");
      done();
    } catch (err) { setError(failText(err)); }
  };
  const remove = async () => {
    if (!category) return;
    try { await del(`${base}/categories/${category.id}`); toast("Gelöscht"); done(); } catch (err) { setError(failText(err)); }
  };
  return (
    <Card
      title={category ? "Kategorie bearbeiten" : "Neue Kategorie"}
      foot={<>
        <Link className="pf-btn" to={here}>Abbrechen</Link>
        {category && <ConfirmButton className="pf-btn pf-btn--danger" confirmLabel="Wirklich löschen?" onConfirm={remove}>Löschen</ConfirmButton>}
        <button type="button" className="pf-btn pf-btn--primary" onClick={() => void save()}>Speichern</button>
      </>}
    >
      <form className="pf-list" noValidate onSubmit={(e) => e.preventDefault()}>
        <div className="pf-grid">
          <label className="pf-field"><span className="pf-field__label">Name (Deutsch)</span><Input name="name_de" value={s.name_de} onChange={(e) => set({ name_de: e.target.value })} /></label>
          <label className="pf-field"><span className="pf-field__label">Name (Englisch)</span><Input name="name_en" value={s.name_en} onChange={(e) => set({ name_en: e.target.value })} /></label>
          <label className="pf-field"><span className="pf-field__label">Position</span><Input name="position" type="number" value={s.position} onChange={(e) => set({ position: e.target.value })} /></label>
        </div>
        <span className="pf-field__error">{error}</span>
      </form>
    </Card>
  );
}

export function TemplateEditor({ template, categories, base, here, done }: EditorProps & { template: PromptTemplate | null; categories: TemplateCategory[] }) {
  const [s, setS] = useState<TemplateState>(() => templateFrom(template, categories));
  const [error, setError] = useState("");
  const set = (p: Partial<TemplateState>) => setS((cur) => ({ ...cur, ...p }));
  if (!categories.length) {
    return <Card title="Neue Vorlage" foot={<Link className="pf-btn" to={`${here}?category=new`}>Neue Kategorie</Link>}><div className="pf-note">Erst eine Kategorie anlegen.</div></Card>;
  }
  const save = async () => {
    try {
      if (template) await patch(`${base}/${template.id}`, templateBody(s)); else await post(base, templateBody(s));
      toast("Gespeichert");
      done();
    } catch (err) { setError(failText(err)); }
  };
  const remove = async () => {
    if (!template) return;
    try { await del(`${base}/${template.id}`); toast("Gelöscht"); done(); } catch (err) { setError(failText(err)); }
  };
  const text = (label: string, key: keyof Omit<TemplateState, "featured" | "category_id">, cls = "pf-input--prose", wide = false) => (
    <label className={`pf-field${wide ? " pf-field--wide" : ""}`}><span className="pf-field__label">{label}</span>
      <Textarea className={cls} name={key} value={s[key]} onChange={(e) => set({ [key]: e.target.value })} /></label>
  );
  return (
    <Card
      title={template ? "Vorlage bearbeiten" : "Neue Vorlage"}
      hint="Der Prompt geht beim Anwenden als erste Nachricht in die neue Unterhaltung — er wird nicht übersetzt."
      foot={<>
        <Link className="pf-btn" to={here}>Abbrechen</Link>
        {template && <ConfirmButton className="pf-btn pf-btn--danger" confirmLabel="Wirklich löschen?" onConfirm={remove}>Löschen</ConfirmButton>}
        <button type="button" className="pf-btn pf-btn--primary" onClick={() => void save()}>Speichern</button>
      </>}
    >
      <form className="pf-list" noValidate onSubmit={(e) => e.preventDefault()}>
        <div className="pf-grid">
          <label className="pf-field"><span className="pf-field__label">Kategorie</span>
            <SelectMenu name="category_id" ariaLabel="Kategorie" value={s.category_id} options={categories.map((c) => [String(c.id), txt(c.name)] as const)} onChange={(category_id) => set({ category_id })} /></label>
          <label className="pf-field"><span className="pf-field__label">Position</span><Input name="position" type="number" value={s.position} onChange={(e) => set({ position: e.target.value })} /></label>
          <label className="pf-field"><span className="pf-field__label">Titel (Deutsch)</span><Input name="title_de" value={s.title_de} onChange={(e) => set({ title_de: e.target.value })} /></label>
          <label className="pf-field"><span className="pf-field__label">Titel (Englisch)</span><Input name="title_en" value={s.title_en} onChange={(e) => set({ title_en: e.target.value })} /></label>
          {text("Beschreibung (Deutsch)", "desc_de")}
          {text("Beschreibung (Englisch)", "desc_en")}
          {text("Voraussetzungen (Deutsch, eine je Zeile)", "deps_de")}
          {text("Voraussetzungen (Englisch, eine je Zeile)", "deps_en")}
          {text("Prompt", "prompt", "pf-input--tall", true)}
        </div>
        <label className="pf-check">
          <input type="checkbox" name="featured" checked={s.featured} onChange={(e) => set({ featured: e.target.checked })} />
          <span className="pf-check__text"><span>Hervorgehoben</span><span className="pf-check__hint">erscheint als „Empfohlen" und ist im Filter vorausgewählt</span></span>
        </label>
        <span className="pf-field__error">{error}</span>
      </form>
    </Card>
  );
}
